import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { usePageTitle } from '@/hooks/use-page-title'
import { useIsMobile } from '@/hooks/use-mobile'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/shadcn/card'
import { Button } from '@/components/shadcn/button'
import { Input } from '@/components/shadcn/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { Checkbox } from '@/components/shadcn/checkbox'
import { DropdownMenu, DropdownMenuContent, DropdownMenuCheckboxItem, DropdownMenuTrigger } from '@/components/shadcn/dropdown-menu'
import { Alert, AlertDescription, AlertTitle } from '@/components/shadcn/alert'
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from '@/components/shadcn/chart'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, BarChart, Bar } from 'recharts'
import { listMemoryLibraryIds } from '@/lib/api/memory'
import {
  fetchTelemetryOverview,
  fetchTelemetryTimeseries,
  fetchTelemetryNewMessagesTimeseries,
  fetchTelemetryMemoryGroupsOverview,
  fetchTelemetryActivitySummary,
  type TelemetryTimeseriesPoint,
  type TelemetryNewMessagesPoint,
} from '@/lib/api/telemetry'
import { Calendar, Filter, RefreshCw } from 'lucide-react'

function toIsoOrUndefined(v: string): string | undefined {
  if (!v) return undefined
  try {
    const d = new Date(v)
    if (isNaN(d.getTime())) return undefined
    return d.toISOString()
  } catch {
    return undefined
  }
}

function isoToLocalDateTime(iso?: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  const yyyy = d.getFullYear()
  const mm = pad(d.getMonth() + 1)
  const dd = pad(d.getDate())
  const hh = pad(d.getHours())
  const mi = pad(d.getMinutes())
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`
}

export default function UsageOverviewPage() {
  const { t } = useTranslation('common')
  const isMobile = useIsMobile()
  
  // 设置页面标题
  useDocumentTitle(t('usage.overview.title'))
  
  // 在移动端设置页面标题到顶栏
  usePageTitle('usage.overview.title')
  
  // Filters
  const { data: allMemoryIds } = useQuery({
    queryKey: ['memory-library-ids'],
    queryFn: () => listMemoryLibraryIds(),
  })

  const [selectedMemoryIds, setSelectedMemoryIds] = useState<string[]>([])
  const [reqType, setReqType] = useState<string>('')
  const [interval, setInterval] = useState<'minute' | 'hour' | 'day'>('hour')

  // Default to last 24h
  const [startLocal, setStartLocal] = useState<string>(() => {
    const d = new Date()
    d.setHours(d.getHours() - 24)
    return isoToLocalDateTime(d.toISOString())
  })
  const [endLocal, setEndLocal] = useState<string>(() => isoToLocalDateTime(new Date().toISOString()))

  useEffect(() => {
    if (allMemoryIds && allMemoryIds.length > 0 && selectedMemoryIds.length === 0) {
      setSelectedMemoryIds(allMemoryIds)
    }
  }, [allMemoryIds])

  const commonParams = useMemo(() => {
    const start = toIsoOrUndefined(startLocal)
    const end = toIsoOrUndefined(endLocal)
    return {
      start,
      end,
      req_type: reqType || undefined,
      memory_ids: (selectedMemoryIds && selectedMemoryIds.length > 0) ? selectedMemoryIds : (allMemoryIds || []),
    }
  }, [startLocal, endLocal, reqType, selectedMemoryIds, allMemoryIds])

  const { data: overview, isLoading: loadingOverview } = useQuery({
    queryKey: ['telemetry-overview', commonParams],
    queryFn: () => fetchTelemetryOverview(commonParams),
    enabled: (allMemoryIds || []).length > 0,
  })

  const { data: timeseries, isLoading: loadingTs } = useQuery({
    queryKey: ['telemetry-timeseries', { ...commonParams, interval }],
    queryFn: () => fetchTelemetryTimeseries({ ...commonParams, interval }),
    enabled: (allMemoryIds || []).length > 0,
  })

  const { data: newMsgTs, isLoading: loadingNewMsg } = useQuery({
    queryKey: ['telemetry-new-messages', { ...commonParams, interval }],
    queryFn: () => fetchTelemetryNewMessagesTimeseries({ ...commonParams, interval }),
    enabled: (allMemoryIds || []).length > 0,
  })

  const { data: groupsOverview } = useQuery({
    queryKey: ['telemetry-memory-groups-overview', commonParams],
    queryFn: () => fetchTelemetryMemoryGroupsOverview(commonParams),
    enabled: (allMemoryIds || []).length > 0,
  })

  const { data: activitySummary } = useQuery({
    queryKey: ['telemetry-activity-summary', { ...commonParams, windows: ['1h','24h','7d','30d'] }],
    queryFn: () => fetchTelemetryActivitySummary({ ...commonParams, windows: ['1h','24h','7d','30d'] }),
    enabled: (allMemoryIds || []).length > 0,
  })

  const isSafeEmpty = useMemo(() => {
    if (!overview) return false
    const zeroOverview = overview.total_requests === 0 && overview.error_count === 0 && Object.keys(overview.by_type || {}).length === 0
    const emptySeries = (timeseries?.length || 0) === 0 && (newMsgTs?.length || 0) === 0
    return zeroOverview && emptySeries
  }, [overview, timeseries, newMsgTs])

  const resetToPreset = (hours: number) => {
    const now = new Date()
    const start = new Date(now.getTime() - hours * 3600 * 1000)
    setStartLocal(isoToLocalDateTime(start.toISOString()))
    setEndLocal(isoToLocalDateTime(now.toISOString()))
  }

  return (
    <div className={`space-y-6 ${isMobile ? 'pt-2' : 'pt-8'}`}>
      <div className="flex flex-col items-stretch space-y-0 sm:flex-row">
        <div className="flex flex-1 flex-col justify-center gap-1">
          <h1 className={`text-2xl font-semibold tracking-tight ${isMobile ? 'hidden' : ''}`}>{t('usage.overview.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('usage.overview.description')}</p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>{t('usage.overview.filters.title')}</CardTitle>
          <CardDescription>{t('usage.overview.filters.description')}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-4">
          <div className="flex flex-col gap-2">
            <div className="text-sm text-muted-foreground">{t('usage.overview.filters.memoryVaults')}</div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="justify-between">
                  <Filter className="mr-2 h-4 w-4" />
                  {selectedMemoryIds.length === (allMemoryIds?.length || 0) ? t('usage.overview.filters.all') : t('usage.overview.filters.selected', { count: selectedMemoryIds.length })}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-64 p-1">
                {(allMemoryIds || []).map((id) => (
                  <DropdownMenuCheckboxItem
                    key={id}
                    checked={selectedMemoryIds.includes(id)}
                    onCheckedChange={(checked) => {
                      setSelectedMemoryIds((prev) => {
                        if (checked) return Array.from(new Set([...prev, id]))
                        return prev.filter((x) => x !== id)
                      })
                    }}
                  >
                    <code className="text-xs">{id}</code>
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="flex flex-col gap-2">
            <div className="text-sm text-muted-foreground">{t('usage.overview.filters.requestType')}</div>
            <Select value={reqType || '_all'} onValueChange={(v) => setReqType(v === '_all' ? '' : v)}>
              <SelectTrigger>
                <SelectValue placeholder={t('usage.overview.filters.all')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">{t('usage.overview.filters.all')}</SelectItem>
                <SelectItem value="memory_query">memory_query</SelectItem>
                <SelectItem value="assistant_message">assistant_message</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <div className="text-sm text-muted-foreground">{t('usage.overview.filters.timeStart')}</div>
            <Input type="datetime-local" value={startLocal} onChange={(e) => setStartLocal(e.target.value)} />
          </div>
          <div className="flex flex-col gap-2">
            <div className="text-sm text-muted-foreground">{t('usage.overview.filters.timeEnd')}</div>
            <div className="flex items-center gap-2">
              <Input type="datetime-local" value={endLocal} onChange={(e) => setEndLocal(e.target.value)} />
              <Button variant="outline" size="icon" onClick={() => setEndLocal(isoToLocalDateTime(new Date().toISOString()))} title={t('usage.overview.filters.resetToCurrent')}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="flex flex-col gap-2 md:col-span-2">
            <div className="text-sm text-muted-foreground">{t('usage.overview.filters.quickRanges')}</div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => resetToPreset(1)}>{t('usage.overview.filters.last1Hour')}</Button>
              <Button variant="outline" size="sm" onClick={() => { setInterval('hour'); resetToPreset(24) }}>{t('usage.overview.filters.last24Hours')}</Button>
              <Button variant="outline" size="sm" onClick={() => { setInterval('day'); resetToPreset(24 * 7) }}>{t('usage.overview.filters.last7Days')}</Button>
              <Button variant="outline" size="sm" onClick={() => { setInterval('day'); resetToPreset(24 * 30) }}>{t('usage.overview.filters.last30Days')}</Button>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <div className="text-sm text-muted-foreground">{t('usage.overview.filters.timeGranularity')}</div>
            <Select value={interval} onValueChange={(v) => setInterval(v as any)}>
              <SelectTrigger>
                <SelectValue placeholder="hour" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="minute">{t('usage.overview.filters.minute')}</SelectItem>
                <SelectItem value="hour">{t('usage.overview.filters.hour')}</SelectItem>
                <SelectItem value="day">{t('usage.overview.filters.day')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {isSafeEmpty && (
        <Alert>
          <AlertTitle>{t('usage.overview.noData.title')}</AlertTitle>
          <AlertDescription>{t('usage.overview.noData.description')}</AlertDescription>
        </Alert>
      )}

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">{t('usage.overview.summary.totalRequests')}</CardTitle>
            <CardDescription className="text-xs text-muted-foreground">{t('usage.overview.summary.totalRequestsDesc')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overview?.total_requests?.toLocaleString?.() ?? '—'}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">{t('usage.overview.summary.errorRate')}</CardTitle>
            <CardDescription className="text-xs text-muted-foreground">{t('usage.overview.summary.errorRateDesc')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overview ? `${(overview.error_rate * 100).toFixed(2)}%` : '—'}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">{t('usage.overview.summary.avgRps')}</CardTitle>
            <CardDescription className="text-xs text-muted-foreground">{t('usage.overview.summary.avgRpsDesc')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overview ? overview.rps_avg.toFixed(3) : '—'}</div>
          </CardContent>
        </Card>
      </div>

      {/* Requests timeseries */}
      <Card>
        <CardHeader>
          <CardTitle>{t('usage.overview.charts.requestDuration.title')}</CardTitle>
          <CardDescription>{t('usage.overview.charts.requestDuration.description')}</CardDescription>
        </CardHeader>
        <CardContent className="px-2 sm:p-6">
          <ChartContainer
            config={{
              avg_ms: { label: 'avg_ms', color: 'hsl(var(--chart-1))' },
              p95_ms: { label: 'p95_ms', color: 'hsl(var(--chart-2))' },
              p99_ms: { label: 'p99_ms', color: 'hsl(var(--chart-3))' },
              count: { label: 'count', color: 'hsl(var(--chart-4))' },
            }}
            className="aspect-auto h-[320px] w-full"
          >
            <LineChart data={timeseries as TelemetryTimeseriesPoint[] | undefined} margin={{ left: 8, right: 8, top: 8, bottom: 8 }}>
              <CartesianGrid vertical={false} />
              <XAxis dataKey="time" tickLine={false} axisLine={false} tickMargin={8} minTickGap={16}
                tickFormatter={(value) => new Date(value).toLocaleString('zh-CN', { hour12: false })}
              />
              <YAxis yAxisId="ms" orientation="left" stroke="hsl(var(--muted-foreground))" />
              <YAxis yAxisId="count" orientation="right" stroke="hsl(var(--muted-foreground))" />
              <ChartTooltip content={<ChartTooltipContent className="w-[200px]" />} />
              <Line yAxisId="ms" type="monotone" dataKey="avg_ms" stroke="var(--color-avg_ms)" dot={false} />
              <Line yAxisId="ms" type="monotone" dataKey="p95_ms" stroke="var(--color-p95_ms)" dot={false} />
              <Line yAxisId="ms" type="monotone" dataKey="p99_ms" stroke="var(--color-p99_ms)" dot={false} />
              <Line yAxisId="count" type="monotone" dataKey="count" stroke="var(--color-count)" dot={false} />
            </LineChart>
          </ChartContainer>
        </CardContent>
      </Card>

      {/* New messages timeseries */}
      <Card>
        <CardHeader>
          <CardTitle>{t('usage.overview.charts.newMessages.title')}</CardTitle>
          <CardDescription>{t('usage.overview.charts.newMessages.description')}</CardDescription>
        </CardHeader>
        <CardContent className="px-2 sm:p-6">
          <ChartContainer
            config={{ count: { label: 'count', color: 'hsl(var(--chart-1))' } }}
            className="aspect-auto h-[260px] w-full"
          >
            <BarChart data={newMsgTs as TelemetryNewMessagesPoint[] | undefined} margin={{ left: 8, right: 8, top: 8, bottom: 8 }}>
              <CartesianGrid vertical={false} />
              <XAxis dataKey="time" tickLine={false} axisLine={false} tickMargin={8} minTickGap={16}
                tickFormatter={(value) => new Date(value).toLocaleString('zh-CN', { hour12: false })}
              />
              <ChartTooltip content={<ChartTooltipContent className="w-[160px]" />} />
              <Bar dataKey="count" fill="var(--color-count)" />
            </BarChart>
          </ChartContainer>
        </CardContent>
      </Card>

      {/* Groups & Activity */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('usage.overview.charts.memoryGroups.title')}</CardTitle>
            <CardDescription>{t('usage.overview.charts.memoryGroups.description')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <div className="text-sm text-muted-foreground">{t('usage.overview.charts.memoryGroups.totalGroups')}</div>
                <div className="text-2xl font-bold">{groupsOverview?.total_groups ?? '—'}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">{t('usage.overview.charts.memoryGroups.newCreated')}</div>
                <div className="text-2xl font-bold">{groupsOverview?.created_in_window ?? '—'}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">{t('usage.overview.charts.memoryGroups.growthRatePerHour')}</div>
                <div className="text-2xl font-bold">{groupsOverview ? groupsOverview.rate_per_hour.toFixed(2) : '—'}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('usage.overview.charts.activity.title')}</CardTitle>
            <CardDescription>{t('usage.overview.charts.activity.description')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              {(activitySummary?.windows || []).map((w) => (
                <div key={w.window} className="border rounded-lg p-3">
                  <div className="text-xs text-muted-foreground">{t('usage.overview.charts.activity.window', { window: w.window })}</div>
                  <div className="mt-2 grid grid-cols-3 gap-2 text-center">
                    <div>
                      <div className="text-[10px] text-muted-foreground">{t('usage.overview.charts.activity.activeUsers')}</div>
                      <div className="text-lg font-bold">{w.active_users_count}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-muted-foreground">{t('usage.overview.charts.activity.messages')}</div>
                      <div className="text-lg font-bold">{w.messages_count}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-muted-foreground">{t('usage.overview.charts.activity.memoryGroups')}</div>
                      <div className="text-lg font-bold">{w.memory_groups_count}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}


